import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiConfig } from "../config/api.config";

function formatCOP(value) {
  if (value === null || value === undefined || isNaN(value)) return "$ 0";
  return new Intl.NumberFormat("es-CO", {
    style: "currency", currency: "COP",
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(value);
}

function formatPeriodo(p) {
  if (!p) return "";
  const [y, m] = p.split("-");
  const meses = ["","Enero","Febrero","Marzo","Abril","Mayo","Junio",
    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
  return `${meses[parseInt(m)]} ${y}`;
}

const VIGENCIA_ACTUAL = new Date().getFullYear();

const TOPES_BASE = {
  Tercera: { base2024: 350, topeFinal: 400, incremento: 10 },
  Cuarta:  { base2024: 280, topeFinal: 330, incremento: 10 },
  Quinta:  { base2024: 190, topeFinal: 210, incremento: 10 },
  Sexta:   { base2024: 150, topeFinal: 200, incremento: 10 },
};

const PCT_ICLD = { Especial: 1.6, Primera: 1.7, Segunda: 2.2 };

function ResultadoIndicador({ label, limite, pptoSec20, detalleLimite }) {
  const diferencia = limite - pptoSec20;
  const alerta     = diferencia < 0;
  return (
    <div style={{ marginTop: "1.25rem" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
        <tbody>
          <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
            <td style={{ padding: "0.75rem 0.5rem", color: "#6b7280" }}>
              <strong>A</strong> — Límite máximo ({label})
              <div style={{ fontSize: "0.8rem", color: "#9ca3af" }}>{detalleLimite}</div>
            </td>
            <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
              {formatCOP(limite)}
            </td>
          </tr>
          <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
            <td style={{ padding: "0.75rem 0.5rem", color: "#6b7280" }}>
              <strong>B</strong> — Presupuesto definitivo sección 20 (personería)
              <div style={{ fontSize: "0.8rem", color: "#9ca3af" }}>
                Suma ptto definitivo sección presupuestal 20
              </div>
            </td>
            <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
              {formatCOP(pptoSec20)}
            </td>
          </tr>
          <tr style={{ background: alerta ? "#fef2f2" : "#f0fdf4" }}>
            <td style={{ padding: "0.75rem 0.5rem", fontWeight: 700, color: "#163b8c" }}>
              Diferencia (A − B)
              <div style={{ fontSize: "0.8rem", fontWeight: 400, color: "#6b7280", marginTop: "0.2rem" }}>
                {alerta ? "⚠️ El gasto supera el límite legal." : "✅ El gasto está dentro del límite legal."}
              </div>
            </td>
            <td style={{ padding: "0.75rem 0.5rem", textAlign: "right",
              fontWeight: 800, fontSize: "1.15rem",
              color: alerta ? "#dc2626" : "#16a34a" }}>
              {formatCOP(diferencia)}
            </td>
          </tr>
        </tbody>
      </table>

      <div style={{ marginTop: "1rem", padding: "0.85rem 1rem", borderRadius: "10px",
        background: alerta ? "#fef2f2" : "#f0fdf4",
        border: `1px solid ${alerta ? "#fca5a5" : "#86efac"}`,
        fontSize: "0.9rem", color: alerta ? "#991b1b" : "#166534" }}>
        {alerta ? (
          <>
            <strong>⚠️ Alerta:</strong> El presupuesto de la personería supera el límite legal en{" "}
            <strong>{formatCOP(Math.abs(diferencia))}</strong>.
            Se debe realizar una reducción presupuestal para cumplir el indicador.
          </>
        ) : (
          <>
            <strong>✅ Dentro del límite.</strong> Hay un margen disponible de{" "}
<strong>{formatCOP(diferencia)}</strong> que puede ser adicionado.
          </>
        )}
      </div>
    </div>
  );
}

export default function PersoneriaIndicadores() {
  const navigate = useNavigate();

  const [smmlvs, setSmmlvs]           = useState([]);
  const [presupuesto, setPresupuesto] = useState(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [mensajeOk, setMensajeOk]     = useState("");

  // Bloque 1 — Especial/Primera/Segunda
  const [vigencia1, setVigencia1]   = useState(VIGENCIA_ACTUAL);
  const [categoria1, setCategoria1] = useState("Especial");

  // Bloque 2 — Tercera a Sexta
  const [vigencia2, setVigencia2]   = useState(VIGENCIA_ACTUAL);
  const [categoria2, setCategoria2] = useState("Tercera");

  const [mostrarSmmlv, setMostrarSmmlv] = useState(false);
  const [nuevaVig, setNuevaVig]         = useState(VIGENCIA_ACTUAL + 1);
  const [nuevoSmmlv, setNuevoSmmlv]     = useState("");

  const cargarDatos = () => {
    setLoading(true);
    fetch(apiConfig.getUrl("/api/hacienda/personeria/"), { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        setSmmlvs(data.smmlvs || []);
        setPresupuesto(data.presupuesto || null);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { cargarDatos(); }, []);

  const getSmmlv = (vig) => {
    const reg = smmlvs.find(s => parseInt(s.vigencia) === vig);
    return reg ? parseFloat(reg.valor_smmlv) : 0;
  };

  const getSmmlvAutorizados = (cat, vig) => {
    const tope = TOPES_BASE[cat];
    if (!tope) return 0;
    const anos = vig - 2024;
    return Math.min(tope.base2024 + (anos * tope.incremento), tope.topeFinal);
  };

  const icldRecaudo = presupuesto?.icld_recaudo || 0;
  const pptoSec20   = presupuesto?.ppto_sec20 || 0;

  // Cálculo bloque 1
  const limite1 = icldRecaudo * (PCT_ICLD[categoria1] / 100);

  // Cálculo bloque 2
  const valorSmmlv2      = getSmmlv(vigencia2);
  const smmlvAutorizados2 = getSmmlvAutorizados(categoria2, vigencia2);
  const limite2          = smmlvAutorizados2 * valorSmmlv2;

  const handleGuardarSmmlv = async () => {
    if (!nuevoSmmlv) return;
    try {
      const res = await fetch(apiConfig.getUrl("/api/hacienda/personeria/"), {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accion: "guardar_smmlv", vigencia: nuevaVig, valor_smmlv: parseFloat(nuevoSmmlv) }),
      });
      const data = await res.json();
      if (data.ok) { setMensajeOk("SMMLV guardado ✅"); setNuevoSmmlv(""); cargarDatos(); }
      else setMensajeOk("Error: " + data.error);
    } catch (e) { setMensajeOk("Error: " + e.message); }
  };

  const card = {
    background: "#fff", borderRadius: "16px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    padding: "1.5rem", marginBottom: "1.5rem",
  };
  const inputStyle = {
    border: "1px solid #d1d5db", borderRadius: "8px",
    padding: "0.5rem 0.75rem", fontSize: "0.95rem",
    width: "100%", boxSizing: "border-box",
  };
  const labelStyle = {
    display: "block", fontWeight: 600,
    color: "#374151", marginBottom: "0.35rem", fontSize: "0.9rem",
  };

  if (loading) return <div style={{ padding: "3rem", textAlign: "center", color: "#6b7280" }}>Cargando...</div>;

  return (
    <div style={{ minHeight: "100vh", background: "#f3f4f6", padding: "2rem 1.5rem" }}>
      <div style={{ maxWidth: "960px", margin: "0 auto" }}>

        <button type="button" onClick={() => navigate("/hacienda-presupuesto")}
          style={{ background: "#e5e7eb", color: "#111827", border: "none",
            borderRadius: "10px", padding: "0.7rem 1rem",
            cursor: "pointer", fontWeight: 600, marginBottom: "1.5rem" }}>
          ← Volver a Hacienda
        </button>

        <div style={{ marginBottom: "1.5rem" }}>
          <h1 style={{ margin: 0, color: "#163b8c", fontSize: "2rem", fontWeight: 800 }}>
            Personería Municipal
          </h1>
          <p style={{ marginTop: "0.5rem", color: "#6b7280" }}>
            Límite al gasto — Art. 10 Ley 2422/2024 |
            Periodo referencia: <strong>{formatPeriodo(presupuesto?.periodo)}</strong>
          </p>
        </div>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5",
          borderRadius: "12px", padding: "1rem", color: "#991b1b", marginBottom: "1rem" }}>⚠️ {error}</div>}

        {mensajeOk && <div style={{
          background: mensajeOk.includes("Error") ? "#fef2f2" : "#f0fdf4",
          border: `1px solid ${mensajeOk.includes("Error") ? "#fca5a5" : "#86efac"}`,
          borderRadius: "12px", padding: "1rem",
          color: mensajeOk.includes("Error") ? "#991b1b" : "#166534",
          marginBottom: "1rem", fontWeight: 600 }}>{mensajeOk}</div>}

        {/* ══════════════════════════════════════════════
            BLOQUE 1 — Especial, Primera, Segunda (% ICLD)
        ══════════════════════════════════════════════ */}
        <div style={card}>
          <h2 style={{ margin: "0 0 0.5rem", color: "#163b8c", fontSize: "1.15rem" }}>
            Categorías Especial, Primera y Segunda
          </h2>
          <p style={{ margin: "0 0 1.25rem", fontSize: "0.85rem", color: "#6b7280" }}>
            El límite se calcula como porcentaje de los Ingresos Corrientes de Libre Destinación (fuente 11001).
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div>
              <label style={labelStyle}>Vigencia</label>
              <input type="number" value={vigencia1} min={2024} max={2100}
                onChange={e => setVigencia1(parseInt(e.target.value))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Categoría</label>
              <select value={categoria1} onChange={e => setCategoria1(e.target.value)} style={inputStyle}>
                {["Especial","Primera","Segunda"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Info */}
          <div style={{ marginTop: "1rem", padding: "0.75rem 1rem", background: "#f0f4ff",
            borderRadius: "10px", fontSize: "0.88rem", color: "#374151" }}>
            Categoría <strong>{categoria1}</strong>: aplica el{" "}
            <strong>{PCT_ICLD[categoria1]}%</strong> sobre{" "}
            <strong>{formatCOP(icldRecaudo)}</strong> (recaudo ICLD {formatPeriodo(presupuesto?.periodo)})
          </div>

          <ResultadoIndicador
            label={`${PCT_ICLD[categoria1]}% ICLD — ${categoria1}`}
            limite={limite1}
            pptoSec20={pptoSec20}
            detalleLimite={`${PCT_ICLD[categoria1]}% × ${formatCOP(icldRecaudo)} (recaudo fuente 11001)`}
          />
        </div>

        {/* ══════════════════════════════════════════════
            BLOQUE 2 — Tercera a Sexta (SMMLV)
        ══════════════════════════════════════════════ */}
        <div style={card}>
          <h2 style={{ margin: "0 0 0.5rem", color: "#163b8c", fontSize: "1.15rem" }}>
            Categorías Tercera, Cuarta, Quinta y Sexta
          </h2>
          <p style={{ margin: "0 0 1.25rem", fontSize: "0.85rem", color: "#6b7280" }}>
            El límite se calcula en SMMLV (sin auxilio de transporte) con incremento progresivo de 10 SMMLV/año
            desde 2025 según parágrafo 2 Ley 2422/2024.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <div>
              <label style={labelStyle}>Vigencia</label>
              <input type="number" value={vigencia2} min={2024} max={2100}
                onChange={e => setVigencia2(parseInt(e.target.value))} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Categoría</label>
              <select value={categoria2} onChange={e => setCategoria2(e.target.value)} style={inputStyle}>
                {["Tercera","Cuarta","Quinta","Sexta"].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Info SMMLV */}
          <div style={{ marginTop: "1rem", padding: "0.75rem 1rem", background: "#f0f4ff",
            borderRadius: "10px", fontSize: "0.88rem", color: "#374151" }}>
            Tope vigencia {vigencia2}: <strong>{smmlvAutorizados2} SMMLV</strong>
            {smmlvAutorizados2 >= TOPES_BASE[categoria2]?.topeFinal && (
              <span style={{ color: "#059669", marginLeft: "0.5rem" }}>✅ Tope máximo alcanzado</span>
            )}
            <span style={{ color: "#6b7280", marginLeft: "0.5rem" }}>
              × {formatCOP(valorSmmlv2)} (SMMLV {vigencia2} sin auxilio de transporte)
            </span>
            {valorSmmlv2 === 0 && (
              <span style={{ color: "#dc2626", marginLeft: "0.5rem" }}>
                ⚠️ No hay SMMLV registrado para {vigencia2}
              </span>
            )}
            <div style={{ marginTop: "0.3rem", color: "#6b7280", fontSize: "0.82rem" }}>
              Base 2024: {TOPES_BASE[categoria2]?.base2024} SMMLV →
              Tope final: {TOPES_BASE[categoria2]?.topeFinal} SMMLV
              (+{TOPES_BASE[categoria2]?.incremento} SMMLV/año desde 2025)
            </div>
          </div>

          <ResultadoIndicador
            label={`${smmlvAutorizados2} SMMLV — ${categoria2}`}
            limite={limite2}
            pptoSec20={pptoSec20}
            detalleLimite={`${smmlvAutorizados2} SMMLV × ${formatCOP(valorSmmlv2)} (sin auxilio de transporte)`}
          />

          {/* Tabla progresión */}
          <div style={{ marginTop: "1.5rem" }}>
            <h3 style={{ margin: "0 0 0.75rem", color: "#163b8c", fontSize: "1rem" }}>
              Progresión tope SMMLV — {categoria2}
            </h3>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.88rem" }}>
              <thead>
                <tr style={{ background: "#f0f4ff" }}>
                  <th style={{ padding: "0.5rem 0.75rem", textAlign: "left" }}>Vigencia</th>
                  <th style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>SMMLV autorizados</th>
                  <th style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>Valor SMMLV</th>
                  <th style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>Límite máximo</th>
                </tr>
              </thead>
              <tbody>
                {[2024,2025,2026,2027,2028,2029].map(v => {
                  const tope      = TOPES_BASE[categoria2];
                  const smmlvAuth = Math.min(tope.base2024 + ((v - 2024) * tope.incremento), tope.topeFinal);
                  const smmlvReg  = smmlvs.find(s => parseInt(s.vigencia) === v);
                  const limiteV   = smmlvReg ? smmlvAuth * parseFloat(smmlvReg.valor_smmlv) : null;
                  const esActual  = v === vigencia2;
                  return (
                    <tr key={v} style={{
                      borderBottom: "1px solid #e5e7eb",
                      background: esActual ? "#eff6ff" : "transparent",
                      fontWeight: esActual ? 700 : 400,
                    }}>
                      <td style={{ padding: "0.5rem 0.75rem" }}>
                        {v} {esActual && <span style={{ color: "#1d4ed8", fontSize: "0.75rem" }}>← actual</span>}
                      </td>
                      <td style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>
                        {smmlvAuth} SMMLV
                        {smmlvAuth >= tope.topeFinal && (
                          <span style={{ color: "#059669", marginLeft: "0.3rem", fontSize: "0.75rem" }}>✅</span>
                        )}
                      </td>
                      <td style={{ padding: "0.5rem 0.75rem", textAlign: "right", color: "#6b7280" }}>
                        {smmlvReg ? formatCOP(smmlvReg.valor_smmlv) : "—"}
                      </td>
                      <td style={{ padding: "0.5rem 0.75rem", textAlign: "right" }}>
                        {limiteV !== null ? formatCOP(limiteV) : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ── Gestionar SMMLV ── */}
        <div style={card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: mostrarSmmlv ? "1rem" : 0 }}>
            <h2 style={{ margin: 0, color: "#163b8c", fontSize: "1.15rem" }}>
              SMMLV por vigencia (sin auxilio de transporte)
            </h2>
            <button type="button" onClick={() => setMostrarSmmlv(!mostrarSmmlv)}
              style={{ background: "#f0f4ff", color: "#1d4ed8", border: "1px solid #c7d2fe",
                borderRadius: "8px", padding: "0.4rem 0.85rem",
                cursor: "pointer", fontWeight: 600, fontSize: "0.85rem" }}>
              {mostrarSmmlv ? "Ocultar ▲" : "Gestionar ▼"}
            </button>
          </div>
          {mostrarSmmlv && (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem", marginBottom: "1rem" }}>
                <thead>
                  <tr style={{ background: "#f0f4ff" }}>
                    <th style={{ padding: "0.6rem 0.75rem", textAlign: "left" }}>Vigencia</th>
                    <th style={{ padding: "0.6rem 0.75rem", textAlign: "right" }}>Valor SMMLV</th>
                  </tr>
                </thead>
                <tbody>
                  {smmlvs.map(s => (
                    <tr key={s.vigencia} style={{ borderBottom: "1px solid #e5e7eb" }}>
                      <td style={{ padding: "0.6rem 0.75rem", fontWeight: 600 }}>{s.vigencia}</td>
                      <td style={{ padding: "0.6rem 0.75rem", textAlign: "right" }}>
                        {formatCOP(s.valor_smmlv)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: "flex", gap: "0.75rem", alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Vigencia</label>
                  <input type="number" value={nuevaVig} min={2024}
                    onChange={e => setNuevaVig(parseInt(e.target.value))} style={inputStyle} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Valor SMMLV sin auxilio ($)</label>
                  <input type="number" value={nuevoSmmlv} placeholder="ej: 1750905"
                    onChange={e => setNuevoSmmlv(e.target.value)} style={inputStyle} />
                </div>
                <button type="button" onClick={handleGuardarSmmlv}
                  style={{ background: "#059669", color: "#fff", border: "none",
                    borderRadius: "10px", padding: "0.5rem 1rem",
                    cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                  + Guardar
                </button>
              </div>
            </>
          )}
        </div>

      </div>
    </div>
  );
}